/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import EdgeAuthParser from '../../edgeAuth/EdgeAuthParser';
import {EncodedEdgeToken} from '../../edgeAuth/EncodedEdgeToken';

import {
  ISubscribeSuccessResponse,
  ISubscribeWithOfferRequest,
  ISubscribeWithoutOfferRequest
} from '../api/Subscribe';
import {ISetRemoteDescriptionRequest, ISetRemoteDescriptionResponse} from '../api/SetRemoteDescription';
import {IDestroyStreamRequest, IDestroyStreamResponse} from '../api/DestroyStream';
import {IAddIceCandidatesRequest, IAddIceCandidatesResponse} from '../api/AddIceCandidates';
import VersionManager from '../version/VersionManager';
import Parameters from '../../environment/Parameters';
import {BitrateMode, BitrateState, ISetTemporaryMaximalBitrate} from '../api/SetTemporaryMaximalBitrate';
import BitrateModeMapping from './BitrateModeMapping';
import BitrateStateMapping from './BitrateStateMapping';
import {BitsPerSecond, Millisecond} from '../../units/Units';
import {
  IPublishSuccessResponse,
  IPublishWithOfferRequest,
  IPublishWithoutOfferRequest,
  PublishStatus
} from '../api/Publish';

const apiVersion = 7;

export type SubscribeStatus = 'ok' | 'no-stream' | 'not-found' | 'unauthorized' | 'geo-restricted' | 'geo-blocked' | 'rate-limited' | 'capacity' | 'timeout' | 'forbidden-tag' | 'port-in-range-not-found' | 'failed';
export type SetRemoteDescriptionStatus = 'ok' | 'not-found' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'failed';
export type SetTemporaryMaximalBitrateStatus = 'ok' | 'not-found' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'failed';
export type RemoveTemporaryMaximalBitrateStatus = 'ok' | 'not-found' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'failed';
export type AddIceCandidatesStatus = 'ok' | 'not-found' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'failed';
export type DestroyStreamStatus = 'ok' | 'not-found' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'failed';
export interface IStream {
  streamId: string;
  sharedSecret: string;
  tenancy: string;
}

export interface ISubscribeSuccessResponseInit {
  status: 'ok';
  stream: IStream;
  lag: Millisecond;
  rtcConfiguration?: RTCConfiguration;
  setRemoteDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
  createOfferDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
  createAnswerDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
  [key: string]: unknown;
}

export interface ISubscribeFailureResponseInit {
  status: 'no-stream' | 'not-found' | 'unauthorized' | 'geo-restricted' | 'geo-blocked' | 'rate-limited' | 'capacity' | 'timeout' | 'forbidden-tag' | 'port-in-range-not-found' | 'failed';
}

export interface ISubscribeRequestCallback {
  (request: ISubscribeWithOfferRequest | ISubscribeWithoutOfferRequest): Promise<void>;
}

export interface ISubscribeResponseCallback {
  (response: ISubscribeSuccessResponseInit | ISubscribeFailureResponseInit): Promise<void>;
}

export type SubscribeOptions = {
  onSubscribeRequestCallback?: ISubscribeRequestCallback;
  onSubscribeResponseCallback?: ISubscribeResponseCallback;
};

export type IPublishSuccessResponseInit = {
  status: 'ok';
  stream: IStream;
  lag?: Millisecond;
  rtcConfiguration?: RTCConfiguration;
  setRemoteDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
  createOfferDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
  createAnswerDescriptionResponse?: {
    sessionDescription: RTCSessionDescriptionInit;
  };
};

export type IPublishFailureResponseInit = {
  status: 'not-found' | 'geo-restricted' | 'geo-blocked' | 'unauthorized' | 'rate-limited' | 'capacity' | 'timeout' | 'forbidden-tag' | 'missing-channel-or-room-id' | 'port-in-range-not-found' | 'failed';
};

interface ISetRemoteDescriptionResponseInit {
  status: SetRemoteDescriptionStatus;
  sessionDescription?: RTCSessionDescriptionInit;
}

interface IAddIceCandidatesResponseInit {
  status: AddIceCandidatesStatus;
  options?: string[];
}

interface ISetTemporaryMaximalBitrateResponseInit {
  status: SetTemporaryMaximalBitrateStatus;
}

interface IDestroyStreamResponseInit {
  status: DestroyStreamStatus;
}

export default class EndPoint {
  private readonly _logger: ILogger = LoggerFactory.getLogger('EndPoint');
  private readonly _uri: string;
  private readonly _timeout: number;
  private _roundTripTime: number = 0;

  constructor(uri: string, timeout: number) {
    this._uri = uri;
    this._timeout = timeout;

    if (!timeout) {
      throw new Error(`End point requires a timeout`);
    }
  }

  get roundTripTime(): number {
    return this._roundTripTime;
  }

  toString(): string {
    return `EndPoint[uri=${this._uri}]`;
  }

  async ping(): Promise<number> {
    const url = this.buildPingUrl();
    const start = Date.now();
    const response = await Promise.race([
      GlobalScope.value.fetch(url, {
        method: 'GET',
        cache: 'no-cache'
      }),
      new Promise<Response>((_, reject) =>
        GlobalScope.value.setTimeout(() => reject(new Error(`Ping timed out [${url}]`)), this._timeout))
    ]);
    const finished = Date.now();

    if (!response.ok) { /* Handle */
      throw new Error(`Ping failed [${url}] [${response.status}]`);
    }

    this._roundTripTime = finished - start;

    return this._roundTripTime;
  }

  async subscribe(token: EncodedEdgeToken, localSessionDescription: RTCSessionDescriptionInit | null, failureCount: number, options: SubscribeOptions = {}): Promise<ISubscribeSuccessResponseInit | ISubscribeFailureResponseInit> {
    const tenancy = EdgeAuthParser.parseToken(token).tenancy;

    if (!tenancy) {
      this._logger.error('Failed to parse token [%s]', token);

      return {status: 'unauthorized'};
    }

    const url = this.buildUrl([tenancy, 'stream', 'subscribe']).toString();
    const formData = new GlobalScope.value.FormData();
    const clientVersion = VersionManager.sdkVersion;
    let requestBody: ISubscribeWithOfferRequest | ISubscribeWithoutOfferRequest;

    if (failureCount === 0 && localSessionDescription) {
      requestBody = {
        apiVersion,
        clientVersion,
        bearerToken: token,
        failureCount,
        httpRoundTripTime: this._roundTripTime,
        setRemoteDescription: {
          apiVersion,
          sessionDescription: {
            type: localSessionDescription.type,
            sdp: localSessionDescription.sdp
          }
        },
        createAnswerDescription: {apiVersion}
      };
    } else {
      requestBody = {
        apiVersion,
        clientVersion,
        bearerToken: token,
        failureCount,
        httpRoundTripTime: this._roundTripTime,
        createOfferDescription: {apiVersion}
      };
    }

    await options.onSubscribeRequestCallback?.(requestBody);

    formData.append('jsonBody', JSON.stringify(requestBody));

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to subscribe', new Error(`Subscribe timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to subscribe', e);

      return {status: 'failed'};
    }

    const status: SubscribeStatus = this.mapHttpStatusToSubscribeStatus(httpResponse);

    if (status !== 'ok') {
      const response: ISubscribeFailureResponseInit = {status};

      await options.onSubscribeResponseCallback?.(response);

      return response;
    }

    const finished = Date.now();
    const subscribeResponse = await this.convertHttpResponseToSubscribeResponse(tenancy, httpResponse);

    this._logger.debug('Got subscribe response [%j] in [%s] ms', subscribeResponse, finished - start);

    await options.onSubscribeResponseCallback?.(subscribeResponse);

    return subscribeResponse;
  }

  async publish(name: string, token: EncodedEdgeToken, localSessionDescription: RTCSessionDescriptionInit | null, failureCount: number): Promise<IPublishSuccessResponseInit | IPublishFailureResponseInit> {
    const tenancy = EdgeAuthParser.parseToken(token).tenancy;

    if (!tenancy) {
      this._logger.error('Failed to parse token [%s]', token);

      return {status: 'unauthorized'};
    }

    const url = this.buildUrl([tenancy, 'stream', 'publish']).toString();
    const formData = new GlobalScope.value.FormData();
    const clientVersion = VersionManager.sdkVersion;

    if (failureCount === 0 && localSessionDescription) {
      const bodyWithOffer: IPublishWithOfferRequest = {
        apiVersion,
        clientVersion,
        bearerToken: token,
        failureCount,
        httpRoundTripTime: this._roundTripTime,
        name,
        setRemoteDescription: {
          apiVersion,
          sessionDescription: {
            type: localSessionDescription.type,
            sdp: localSessionDescription.sdp
          }
        },
        createAnswerDescription: {
          streamId: '',
          apiVersion
        }
      };

      formData.append('jsonBody', JSON.stringify(bodyWithOffer));
    } else {
      const bodyWithoutOffer: IPublishWithoutOfferRequest = {
        apiVersion,
        clientVersion,
        bearerToken: token,
        failureCount,
        httpRoundTripTime: this._roundTripTime,
        name,
        createOfferDescription: {
          streamId: '',
          apiVersion
        }
      };

      formData.append('jsonBody', JSON.stringify(bodyWithoutOffer));
    }

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to publish', new Error(`Publish timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to publish', e);

      return {status: 'failed'};
    }

    const status: PublishStatus = this.mapHttpStatusToPublishStatus(httpResponse);

    if (status !== 'ok') {
      return {status};
    }

    const finished = Date.now();
    const subscribeResponse = await this.convertHttpResponseToPublishResponse(tenancy, httpResponse);

    this._logger.debug('Got publish response [%j] in [%s] ms', subscribeResponse, finished - start);

    return subscribeResponse;
  }

  async setRemoteDescription(stream: IStream, sessionDescription: RTCSessionDescriptionInit): Promise<ISetRemoteDescriptionResponseInit> {
    const url = this.buildUrl([stream.tenancy, 'stream', stream.streamId, 'description', 'remote']).toString();
    const formData = new GlobalScope.value.FormData();
    const body: ISetRemoteDescriptionRequest = {
      apiVersion,
      sharedSecret: stream?.sharedSecret,
      sessionDescription: {
        type: sessionDescription.type,
        sdp: sessionDescription.sdp
      }
    };

    formData.append('jsonBody', JSON.stringify(body));

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to set remote description', new Error(`Set remote description timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to set remote description', e);

      return {status: 'failed'};
    }

    const status: SetRemoteDescriptionStatus = this.mapHttpStatusToSetRemoteDescriptionStatus(httpResponse);

    if (status !== 'ok') {
      return {status};
    }

    const finished = Date.now();
    const setRemoteDescriptionResponse = await this.convertHttpResponseToSetRemoteDescriptionResponse(httpResponse);

    this._logger.debug('Got set remote description response [%j] in [%s] ms', setRemoteDescriptionResponse, finished - start);

    return setRemoteDescriptionResponse;
  }

  async limitBitrate(
    stream: IStream,
    elapsedInMilliseconds: number,
    bitrateInBitsPerSecond: BitsPerSecond,
    bitrateState: BitrateState,
    bitrateMode: BitrateMode,
  ): Promise<ISetTemporaryMaximalBitrateResponseInit> {
    const url = this.buildUrl([stream.tenancy, 'stream', stream.streamId, 'bitrate']).toString();
    const formData = new GlobalScope.value.FormData();
    const body: ISetTemporaryMaximalBitrate = {
      apiVersion,
      sharedSecret: stream.sharedSecret,
      elapsedInMilliseconds,
      bitrateInBitsPerSecond,
      bitrateState: BitrateStateMapping.convertBitrateStateToBitrateStateType(bitrateState),
      bitrateMode: BitrateModeMapping.convertBitrateModeToBitrateModeType(bitrateMode)
    };

    formData.append('jsonBody', JSON.stringify(body));

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to set limit bitrate timed', new Error(`Set limit bitrate timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to set limit bitrate timed', e);

      return {status: 'failed'};
    }

    const status: SetTemporaryMaximalBitrateStatus = this.mapHttpStatusToSetTemporaryMaximalBitrateStatus(httpResponse);
    const finished = Date.now();

    this._logger.info('Got set limit bitrate response [%s] in [%s] ms', status, finished - start);

    return {status};
  }

  async addIceCandidates(stream: IStream, candidates: RTCIceCandidate[], discoveryCompleted: boolean, options: string[] = []): Promise<IAddIceCandidatesResponseInit> {
    const url = this.buildUrl([stream.tenancy, 'stream', stream.streamId, 'ice', 'candidates']).toString();
    const formData = new GlobalScope.value.FormData();
    const body: IAddIceCandidatesRequest = {
      apiVersion,
      sharedSecret: stream.sharedSecret,
      candidates,
      discoveryCompleted,
      options
    };

    formData.append('jsonBody', JSON.stringify(body));

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to add ice candidates', new Error(`Add ice candidates timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to add ice candidates', e);

      return {status: 'failed'};
    }

    const status: AddIceCandidatesStatus = this.mapHttpStatusToAddIceCandidatesStatus(httpResponse);

    if (status !== 'ok') {
      return {status};
    }

    const finished = Date.now();
    const addIceCandidatesResponse = await this.convertHttpResponseToAddIceCandidatesResponse(httpResponse);

    this._logger.info('Got add ICE candidates response [%j] in [%s] ms', addIceCandidatesResponse, finished - start);

    return addIceCandidatesResponse;
  }

  async destroyStream(stream: IStream, reason: string): Promise<IDestroyStreamResponseInit> {
    const url = this.buildUrl([stream.tenancy, 'stream', stream.streamId, 'destroy']).toString();
    const formData = new GlobalScope.value.FormData();
    const body: IDestroyStreamRequest = {
      apiVersion,
      sharedSecret: stream.sharedSecret,
      reason,
      options: []
    };

    formData.append('jsonBody', JSON.stringify(body));

    const start = Date.now();
    let httpResponse: Response;

    try {
      let timeout: number | null = null;

      httpResponse = await Promise.race([
        GlobalScope.value.fetch(url, {
          method: 'POST',
          body: formData,
          cache: 'no-cache'
        }),
        new Promise<Response>(resolve =>
          timeout = GlobalScope.value.setTimeout(() => {
            this._logger.error('Failed to subscribe', new Error(`Delete stream timed out [${url}]`));
            resolve({status: 408} as Response);
          }, this._timeout))
      ])
        .finally(() => {
          if (timeout) {
            GlobalScope.value.clearTimeout(timeout);
          }
        });
    } catch (e) {
      this._logger.error('Failed to delete stream', e);

      return {status: 'failed'};
    }

    const status: DestroyStreamStatus = this.mapHttpStatusToSetDestroyStreamStatus(httpResponse);

    if (status !== 'ok') {
      return {status};
    }

    const finished = Date.now();
    const destroyStreamResponse = await this.convertHttpResponseToDestroyStreamResponse(httpResponse);

    this._logger.info('Got destroy stream response [%j] in [%s] ms', destroyStreamResponse, finished - start);

    return destroyStreamResponse;
  }

  async destroyStreamOnUnmount(stream: IStream, reason: string): Promise<void> {
    if (!navigator?.sendBeacon) {
      const ignored = this.destroyStream(stream, reason);
    }

    const url = this.buildUrl([stream.tenancy, 'stream', stream.streamId, 'destroy']).toString();
    const formData = new GlobalScope.value.FormData();
    const body: IDestroyStreamRequest = {
      apiVersion,
      sharedSecret: stream.sharedSecret,
      reason,
      options: []
    };

    formData.append('jsonBody', JSON.stringify(body));

    navigator.sendBeacon(url, formData);
  }

  private buildUrl(path: string[]): URL {
    const uri = new GlobalScope.value.URL(this._uri);
    const pathAsArray = uri.pathname.split('/');

    pathAsArray.length = pathAsArray.length - 1;

    uri.pathname = pathAsArray.concat(...path).join('/');

    return uri;
  }

  private buildPingUrl(): string {
    const uri = new GlobalScope.value.URL(this._uri);
    const sdkVersion = VersionManager.sdkVersion;
    const intentions = Parameters.discoveryParameters.intentions;

    uri.searchParams.set('type', 'http');
    uri.searchParams.set('version', sdkVersion);
    uri.searchParams.set('intentions', intentions);
    uri.searchParams.set('_', `${Date.now()}`);

    return uri.toString();
  }

  private mapHttpStatusToPublishStatus(response: Response): PublishStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 402:
        return 'geo-restricted';
      case 403:
        return 'geo-blocked';
      case 406:
        return 'missing-channel-or-room-id';
      case 408:
        return 'timeout';
      case 409:
        return 'port-in-range-not-found';
      case 415:
        return 'forbidden-tag';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private mapHttpStatusToSubscribeStatus(response: Response): SubscribeStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 402:
        return 'geo-restricted';
      case 403:
        return 'geo-blocked';
      case 408:
        return 'timeout';
      case 409:
        return 'port-in-range-not-found';
      case 415:
        return 'forbidden-tag';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private mapHttpStatusToSetRemoteDescriptionStatus(response: Response): SetRemoteDescriptionStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 408:
        return 'timeout';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private mapHttpStatusToSetTemporaryMaximalBitrateStatus(response: Response): SetTemporaryMaximalBitrateStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 408:
        return 'timeout';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private mapHttpStatusToAddIceCandidatesStatus(response: Response): AddIceCandidatesStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 408:
        return 'timeout';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private mapHttpStatusToSetDestroyStreamStatus(response: Response): DestroyStreamStatus {
    if (!response) {
      return 'failed';
    }

    switch (response.status) {
      case 200:
        return 'ok';
      case 401:
        return 'unauthorized';
      case 408:
        return 'timeout';
      case 503:
        return 'capacity';
      case 504:
        return 'rate-limited';
      default:
        return 'failed';
    }
  }

  private async convertHttpResponseToSubscribeResponse(tenancy: string, response: Response): Promise<ISubscribeSuccessResponseInit> {
    const data = await response.json() as ISubscribeSuccessResponse;
    const {
      status,
      streamId,
      sharedSecret,
      lag,
      rtcConfiguration,
      setRemoteDescriptionResponse,
      createAnswerDescriptionResponse,
      createOfferDescriptionResponse,
      ...otherProperties
    } = data;
    const subscribeResponse: ISubscribeSuccessResponseInit = {
      status,
      stream: {
        tenancy,
        streamId,
        sharedSecret
      },
      lag,
      ...otherProperties
    };

    if (rtcConfiguration) {
      subscribeResponse.rtcConfiguration = this.convertIRtcConfigurationToRTCConfiguration(rtcConfiguration);
    }

    if (setRemoteDescriptionResponse?.sessionDescription) {
      subscribeResponse.setRemoteDescriptionResponse = {sessionDescription: setRemoteDescriptionResponse.sessionDescription};
    }

    if (createAnswerDescriptionResponse?.sessionDescription) {
      subscribeResponse.createAnswerDescriptionResponse = {sessionDescription: createAnswerDescriptionResponse.sessionDescription};
    }

    if (createOfferDescriptionResponse?.sessionDescription) {
      subscribeResponse.createOfferDescriptionResponse = {sessionDescription: createOfferDescriptionResponse.sessionDescription};
    }

    return subscribeResponse;
  }

  private async convertHttpResponseToPublishResponse(tenancy: string, response: Response): Promise<IPublishSuccessResponseInit> {
    const data = await response.json() as IPublishSuccessResponse;
    const publishResponse: IPublishSuccessResponseInit = {
      status: data.status,
      stream: {
        tenancy,
        streamId: data.streamId,
        sharedSecret: data.sharedSecret
      }
    };

    if (data) {
      if (data.rtcConfiguration) {
        publishResponse.rtcConfiguration = this.convertIRtcConfigurationToRTCConfiguration(data.rtcConfiguration);
      }

      if (data.setRemoteDescriptionResponse && data.setRemoteDescriptionResponse.sessionDescription) {
        publishResponse.setRemoteDescriptionResponse = {sessionDescription: data.setRemoteDescriptionResponse.sessionDescription};
      }

      if (data.createAnswerDescriptionResponse && data.createAnswerDescriptionResponse.sessionDescription) {
        publishResponse.createAnswerDescriptionResponse = {sessionDescription: data.createAnswerDescriptionResponse.sessionDescription};
      }

      if (data.createOfferDescriptionResponse && data.createOfferDescriptionResponse.sessionDescription) {
        publishResponse.createOfferDescriptionResponse = {sessionDescription: data.createOfferDescriptionResponse.sessionDescription};
      }
    }

    return publishResponse;
  }

  private convertIRtcConfigurationToRTCConfiguration(configuration: RTCConfiguration): RTCConfiguration {
    const rtcConfiguration: RTCConfiguration = {iceServers: []};

    if (configuration.bundlePolicy) {
      rtcConfiguration.bundlePolicy = configuration.bundlePolicy;
    }

    if (typeof configuration.iceCandidatePoolSize === 'number') {
      rtcConfiguration.iceCandidatePoolSize = configuration.iceCandidatePoolSize;
    }

    if (configuration.iceServers) {
      const iceServers: RTCIceServer[] = [];

      for (let i = 0; i < configuration.iceServers.length; i++) {
        iceServers.push({
          urls: configuration.iceServers[i].urls,
          username: configuration.iceServers[i].username,
          credential: configuration.iceServers[i].credential
        });
      }

      rtcConfiguration.iceServers = iceServers;
    }

    if (configuration.iceTransportPolicy) {
      rtcConfiguration.iceTransportPolicy = configuration.iceTransportPolicy;
    }

    if (configuration.rtcpMuxPolicy) {
      rtcConfiguration.rtcpMuxPolicy = configuration.rtcpMuxPolicy;
    }

    return rtcConfiguration;
  }
  private async convertHttpResponseToSetRemoteDescriptionResponse(response: Response): Promise<ISetRemoteDescriptionResponseInit> {
    const data = await response.json() as ISetRemoteDescriptionResponse;
    const setRemoteDescriptionResponse: ISetRemoteDescriptionResponseInit = {status: data.status};

    if (data && data.sessionDescription) {
      setRemoteDescriptionResponse.sessionDescription = data.sessionDescription;
    }

    return setRemoteDescriptionResponse;
  }

  private async convertHttpResponseToAddIceCandidatesResponse(response: Response): Promise<IAddIceCandidatesResponseInit> {
    const data = await response.json() as IAddIceCandidatesResponse;
    const addIceCandidatesResponse: IAddIceCandidatesResponseInit = {
      status: data.status,
      options: []
    };

    if (data) {
      if (data.options) {
        addIceCandidatesResponse.options = data.options;
      }
    }

    return addIceCandidatesResponse;
  }

  private async convertHttpResponseToDestroyStreamResponse(response: Response): Promise<IDestroyStreamResponseInit> {
    const data = await response.json() as IDestroyStreamResponse;
    const destroyStream: IDestroyStreamResponseInit = {status: data.status};

    return destroyStream;
  }
}